import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import { BarChart2, FileText, ExternalLink, Globe, Lock, LayoutList, BookOpen, Sparkles, Download, FileSpreadsheet, FileDown } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useSites } from '@/hooks/useSites'
import { useBlogPosts } from '@/hooks/useBlogPosts'
import {
  usePageViewBreakdown,
  useReferrerBreakdown,
  useDailyViewChart,
  useAnalyticsInsights,
  type AnalyticsInsight,
} from '@/hooks/useAnalytics'
import { downloadCsv, type ExportSection } from '@/lib/csv'
import { downloadXlsx } from '@/lib/xlsx'
import { tenantCanViewAnalytics, tenantCanUseAiAssistant } from '@brix/types'

const BLOG_PREFIX = 'blog/'

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const DAYS_OPTIONS = [7, 14, 30] as const
type Days = (typeof DAYS_OPTIONS)[number]

// ── Simple bar chart (no external chart lib) ──────────────────
function MiniBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
      <div className="h-full rounded-full bg-violet-500" style={{ width: `${pct}%` }} />
    </div>
  )
}

// ── Reusable breakdown list (top pages / top blog posts / referrers) ──
function BreakdownList({ rows, loading, emptyLabel = 'No data yet' }: {
  rows: { key: string; label: string; views: number }[]
  loading: boolean
  emptyLabel?: string
}) {
  const max = Math.max(...rows.map((r) => r.views), 1)

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-gray-100" />)}
      </div>
    )
  }
  if (rows.length === 0) {
    return <p className="py-4 text-center text-sm text-gray-400">{emptyLabel}</p>
  }
  return (
    <ul className="space-y-3">
      {rows.slice(0, 10).map((row) => (
        <li key={row.key}>
          <div className="mb-1 flex items-center justify-between">
            <span className="max-w-[180px] truncate text-sm text-gray-700">{row.label}</span>
            <span className="text-sm font-semibold text-gray-900">{row.views.toLocaleString()}</span>
          </div>
          <MiniBar value={row.views} max={max} />
        </li>
      ))}
    </ul>
  )
}

// ── AI Insights card — refreshed at most once/day server-side ─
function AiInsightsCard({ enabled, data, isLoading, error }: {
  enabled: boolean
  data: AnalyticsInsight | undefined
  isLoading: boolean
  error: unknown
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-500" />
        <h2 className="text-sm font-semibold text-gray-700">AI Insights</h2>
      </div>

      {!enabled ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Lock className="h-6 w-6 text-gray-300" />
          <p className="text-sm text-gray-500">AI-generated insights require the Pro plan</p>
          <Link to="/billing" className="text-sm font-medium text-violet-600 hover:underline">
            View plans →
          </Link>
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-gray-400">Couldn't load insights right now.</p>
      ) : data ? (
        <div>
          <p className="text-sm leading-relaxed text-gray-700">{data.summary}</p>
          {data.recommendations.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {data.recommendations.map((rec, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-600">
                  <span className="text-violet-400">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-gray-400">Updated {fmtRelative(data.generatedAt)}</p>
        </div>
      ) : null}
    </div>
  )
}

// ── Sparkline for daily chart ─────────────────────────────────
function DailySparkline({ data }: { data: { day: string; total_views: number }[] }) {
  if (!data.length) return <p className="text-xs text-gray-400">No data yet</p>

  const max = Math.max(...data.map((d) => d.total_views), 1)
  const H = 48
  const W = 280
  const step = W / (data.length - 1 || 1)

  const points = data
    .map((d, i) => `${Math.round(i * step)},${Math.round(H - (d.total_views / max) * H)}`)
    .join(' ')

  const filled = [
    `0,${H}`,
    ...data.map((d, i) => `${Math.round(i * step)},${Math.round(H - (d.total_views / max) * H)}`),
    `${W},${H}`,
  ].join(' ')

  const total = data.reduce((s, d) => s + d.total_views, 0)
  const firstDay = new Date(data[0]!.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const lastDay = new Date(data[data.length - 1]!.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <p className="text-3xl font-bold text-gray-900">{total.toLocaleString()}</p>
        <span className="text-xs text-gray-400">{firstDay} – {lastDay}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="#7c3aed"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
        <polygon
          fill="#7c3aed"
          fillOpacity="0.08"
          points={filled}
        />
      </svg>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────
export function AnalyticsPage() {
  const { siteId = '' } = useParams()
  const tenant = useAuthStore((s) => s.tenant)
  const { data: sites = [] } = useSites()
  const site = sites.find((s) => s.id === siteId)
  const [days, setDays] = useState<Days>(30)
  const [pdfDownloading, setPdfDownloading] = useState(false)

  const systemAnalytics = import.meta.env['VITE_ANALYTICS_ENABLED'] === 'true'
  const analyticsEnabled = systemAnalytics && !!tenant && tenantCanViewAnalytics(tenant)
  const aiInsightsEnabled = analyticsEnabled && !!tenant && tenantCanUseAiAssistant(tenant)

  const { data: pageBreakdown = [], isLoading: loadingPages } = usePageViewBreakdown(siteId, analyticsEnabled, days)
  const { data: referrers = [], isLoading: loadingRefs } = useReferrerBreakdown(siteId, analyticsEnabled, days)
  const { data: dailyChart = [], isLoading: loadingChart } = useDailyViewChart(siteId, analyticsEnabled, days)
  const { data: blogPosts = [] } = useBlogPosts(siteId)

  const titleBySlug = useMemo(
    () => new Map(blogPosts.map((p) => [p.slug, p.title])),
    [blogPosts],
  )

  const pageRows = useMemo(() => pageBreakdown
    .filter((r) => !r.page_slug.startsWith(BLOG_PREFIX))
    .map((r) => ({ key: r.page_slug, label: `/${r.page_slug}`, views: r.total_views })),
    [pageBreakdown])

  const blogRows = useMemo(() => pageBreakdown
    .filter((r) => r.page_slug.startsWith(BLOG_PREFIX))
    .map((r) => {
      const slug = r.page_slug.slice(BLOG_PREFIX.length)
      return { key: r.page_slug, label: titleBySlug.get(slug) ?? slug, views: r.total_views }
    }),
    [pageBreakdown, titleBySlug])

  const referrerRows = useMemo(() => referrers
    .map((r) => ({ key: r.referrer, label: r.referrer, views: r.total_views })),
    [referrers])

  const totalViews = useMemo(() => dailyChart.reduce((sum, d) => sum + d.total_views, 0), [dailyChart])

  const insightsQuery = useAnalyticsInsights(siteId, aiInsightsEnabled, days)

  const exportSections = useMemo((): ExportSection[] => [
    {
      title: 'Daily views',
      headers: ['Day', 'Views'],
      rows: dailyChart.map((d) => [d.day, d.total_views]),
    },
    {
      title: 'Top pages',
      headers: ['Page', 'Views'],
      rows: pageRows.map((r) => [r.label, r.views]),
    },
    {
      title: 'Top blog posts',
      headers: ['Post', 'Views'],
      rows: blogRows.map((r) => [r.label, r.views]),
    },
    {
      title: 'Top referrers',
      headers: ['Referrer', 'Views'],
      rows: referrerRows.map((r) => [r.label, r.views]),
    },
  ], [dailyChart, pageRows, blogRows, referrerRows])

  const exportFilenameBase = `analytics-${site?.subdomain ?? siteId}-${days}d`

  const handleExportCsv = () => downloadCsv(`${exportFilenameBase}.csv`, exportSections)
  const handleExportXlsx = () => downloadXlsx(`${exportFilenameBase}.xlsx`, exportSections)

  const handleDownloadPdf = async () => {
    const { AnalyticsReportPDF } = await import('@/components/analytics/AnalyticsReportPDF')
    const blob = await pdf(
      <AnalyticsReportPDF
        siteName={site?.name ?? 'Site'}
        siteDomain={site ? `${site.subdomain}.brix.app` : ''}
        periodDays={days}
        totalViews={totalViews}
        topPages={pageRows}
        topBlogPosts={blogRows}
        topReferrers={referrerRows}
        insight={aiInsightsEnabled && insightsQuery.data ? insightsQuery.data : null}
      />,
    ).toBlob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${exportFilenameBase}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-8">
      {/* Breadcrumb + sub-nav */}
      <div className="mb-6">
        <Link to="/sites" className="text-sm text-gray-400 hover:text-violet-600">← Sites</Link>
        <div className="mt-2 flex items-center gap-1 border-b border-gray-200 pb-0">
          <Link
            to={`/sites/${siteId}/pages`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <LayoutList className="h-4 w-4" /> Pages
          </Link>
          <Link
            to={`/sites/${siteId}/blog`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <BookOpen className="h-4 w-4" /> Blog posts
          </Link>
          <span className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 border-b-2 border-violet-600 -mb-px">
            <BarChart2 className="h-4 w-4" /> Analytics
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          {site && (
            <a
              href={`https://${site.subdomain}.brix.app`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 flex items-center gap-1 text-sm text-gray-400 hover:text-violet-600"
            >
              <Globe className="h-3 w-3" />
              {site.subdomain}.brix.app
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {analyticsEnabled && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={[
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors cursor-pointer',
                    days === d
                      ? 'bg-violet-600 text-white'
                      : 'text-gray-500 hover:text-gray-900',
                  ].join(' ')}
                >
                  {d}d
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={handleExportCsv}
                title="Export CSV"
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
              >
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
              <button
                onClick={handleExportXlsx}
                title="Export XLSX"
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" /> XLSX
              </button>
              <button
                onClick={() => { setPdfDownloading(true); handleDownloadPdf().finally(() => setPdfDownloading(false)) }}
                disabled={pdfDownloading}
                title="Download PDF report"
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
              >
                {pdfDownloading ? (
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                ) : (
                  <FileDown className="h-3.5 w-3.5" />
                )}
                PDF
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade gate */}
      {!analyticsEnabled && (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-700">Analytics requires Starter plan or above</p>
          <p className="mt-1 text-sm text-gray-400">
            {!systemAnalytics
              ? 'Analytics tracking is not enabled on this instance.'
              : 'Upgrade your plan to unlock page view analytics.'}
          </p>
          {systemAnalytics && (
            <Link to="/billing" className="mt-4 inline-block text-sm font-medium text-violet-600 hover:underline">
              View plans →
            </Link>
          )}
        </div>
      )}

      {analyticsEnabled && (
        <div className="space-y-6">
          {/* Daily chart */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">Page views — last {days} days</h2>
            {loadingChart ? (
              <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
            ) : dailyChart.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">No views recorded yet</p>
            ) : (
              <DailySparkline data={dailyChart} />
            )}
          </div>

          <AiInsightsCard
            enabled={aiInsightsEnabled}
            data={insightsQuery.data}
            isLoading={insightsQuery.isLoading}
            error={insightsQuery.error}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Top pages */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-700">Top pages</h2>
              </div>
              <BreakdownList rows={pageRows} loading={loadingPages} />
            </div>

            {/* Top blog posts */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-700">Top blog posts</h2>
              </div>
              <BreakdownList rows={blogRows} loading={loadingPages} emptyLabel="No blog views yet" />
            </div>

            {/* Top referrers */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-700">Top referrers</h2>
              </div>
              <BreakdownList rows={referrerRows} loading={loadingRefs} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
